import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
  Box,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { format } from "date-fns";

import { OrganizationApiToken } from "types/Organization";

interface OverviewTabProps {
  token: OrganizationApiToken;
}
export const OverviewTab = ({ token }: OverviewTabProps): JSX.Element => {
  const { t } = useTranslation();
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const timeoutRef = useRef<number>();

  const onCopyClick = useCallback(() => {
    navigator.clipboard.writeText(token.bearerToken);
    setTooltipVisible(true);
    timeoutRef.current = window.setTimeout(
      () => setTooltipVisible(false),
      1000
    );
  }, [token, setTooltipVisible]);

  useEffect(
    () => () => {
      if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
    },
    []
  );
  const expiration = token.expirationDate
    ? new Date(token.expirationDate)
    : undefined;
  const expirationLabel =
    expiration && !Number.isNaN(expiration.getTime())
      ? format(expiration, t("DateFormat"))
      : "";
  const userLabel = token.user
    ? `${token.user.firstName} ${token.user.lastName} (${token.user.email})`
    : "";

  return (
    <Box paddingY={2}>
      <Stack direction="row" justifyContent="space-between">
        <Typography variant="body2">
          {token.name?.toUpperCase()}: {token.id}
        </Typography>
        <Typography variant="body2">
          {t("Expires")}: {expirationLabel}
        </Typography>
      </Stack>
      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid item xs={3} textAlign="right">
          <Typography variant="body2">{t("Application")}:</Typography>
        </Grid>
        <Grid item xs={9}>
          <Typography variant="body2">{token.applicationName}</Typography>
        </Grid>
        <Grid item xs={3} textAlign="right">
          <Typography variant="body2">{t("User")}:</Typography>
        </Grid>
        <Grid item xs={9}>
          <Typography variant="body2">{userLabel}</Typography>
        </Grid>
        <Grid item xs={3} textAlign="right">
          <Typography variant="body2">{t("Description")}:</Typography>
        </Grid>
        <Grid item xs={9}>
          <Typography variant="body2">{token.description}</Typography>
        </Grid>
        <Grid item xs={3} textAlign="right">
          <Typography variant="body2">{t("Token")}:</Typography>
        </Grid>
        <Grid item xs={8}>
          <Typography variant="body2" sx={{ overflowWrap: "break-word" }}>
            {token.bearerToken}
          </Typography>
        </Grid>
        <Grid item xs={1} textAlign="center" alignSelf="center">
          <Tooltip
            title={t("Copied")}
            open={tooltipVisible}
            disableHoverListener
            placement="top"
          >
            <IconButton onClick={onCopyClick}>
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Grid>
      </Grid>
    </Box>
  );
};
